import { create } from 'zustand';
import type { InsightDto, LiveViewDto, TargetProgressDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';

/**
 * The overview's live view, the month against its goal, and suggestions
 * (ADR-121). Each loads on its own, so a slow one never holds the others.
 */
interface InsightsState {
  live: LiveViewDto | null;
  target: TargetProgressDto | null;
  insights: InsightDto[] | null;
  error: string | null;
  loadLive: () => Promise<void>;
  loadTarget: () => Promise<void>;
  loadInsights: () => Promise<void>;
  /** The owner's monthly goals, halalas and orders; null clears one. */
  saveTarget: (goals: { monthlyRevenue: number | null; monthlyOrders: number | null }) => Promise<boolean>;
}

export const useInsightsStore = create<InsightsState>((set, get) => ({
  live: null,
  target: null,
  insights: null,
  error: null,

  async loadLive() {
    try {
      set({ live: await withSession(() => adminApi.reports.live()) });
    } catch {
      // A missed refresh keeps the last figures; the next one tries again.
    }
  },

  async loadTarget() {
    try {
      set({ target: await withSession(() => adminApi.reports.target()) });
    } catch (error) {
      set({ error: errorMessage(error, 'Could not load the goal.') });
    }
  },

  async loadInsights() {
    try {
      set({ insights: await withSession(() => adminApi.reports.insights(useUiStore.getState().locale)) });
    } catch (error) {
      set({ insights: [], error: errorMessage(error, 'Could not load the suggestions.') });
    }
  },

  async saveTarget(goals) {
    try {
      await withSession(() => adminApi.settings.update('reports.targets', goals));
      await Promise.all([get().loadTarget(), get().loadInsights()]);
      return true;
    } catch (error) {
      set({ error: errorMessage(error, 'Could not save the goal.') });
      return false;
    }
  },
}));
